import { UserDto } from '@act/shared/data-transfer-objects';
import { CollapsibleCard, IconLibrary } from '@act/storybook';
import useLinkConsentsDialog from 'react-components/dialog-forms/hooks/use-link-consents-dialog';
import useTextConsentsDialog from 'react-components/dialog-forms/hooks/use-text-consents-dialog';
import useViewConsentsHistoryDialog from 'react-components/dialog-forms/hooks/useViewConsentsHistoryDialog';
import { consentAppSelectors } from 'redux/slices/consent.slice';
import { selectedMemberSelectors } from 'redux/slices/selectedMemberSlice';
import ConsentsList from './ConsentsList';
import { Badge, TaggableChip } from '@act/storybook';
import LanguageSelector from '@act/react-components/consents/member-view/LanguageSelector';

interface ConsentInformationCardProps {
  member: UserDto;
  defaultClosed?: boolean;
  onToggle?: () => void;
}

const ConsentInformationCard = ({
  member,
  defaultClosed = false,
  onToggle,
}: ConsentInformationCardProps) => {
  const openViewConsentsHistoryDialog = useViewConsentsHistoryDialog();

  const terms = consentAppSelectors.useSelectAllTerms();
  const incompleteTermsCount =
    consentAppSelectors.useSelectIncompleteTermsCount();
  const userId = selectedMemberSelectors.useSelectSelectedMemberId();
  const openTextConsentsDialog = useTextConsentsDialog();
  const linkTextConsentsDialog = useLinkConsentsDialog();
  const allTermsAccepted = terms.every(term => term?.consented);
  const allTermsRejected = terms.every(term => term?.consented === false);
  const rejectedTerms = terms.filter(term => term?.consented === false);

  function determineDefaultClosed() {
    return defaultClosed || incompleteTermsCount === 0;
  }

  return (
    <CollapsibleCard
      title={'Consents'}
      badge={
        incompleteTermsCount > 0 && (
          <Badge badgeContent={incompleteTermsCount} color="error" />
        )
      }
      chips={
        <>
          {!!terms.length && allTermsAccepted && (
            <TaggableChip
              color="success"
              variant="filled"
              label={'All accepted'}
              isTag
            />
          )}
          {!!terms.length && allTermsRejected && (
            <TaggableChip
              color="error"
              variant="filled"
              label={'All rejected'}
              isTag
            />
          )}
          {!allTermsRejected &&
            rejectedTerms.map(term => (
              <TaggableChip
                key={term.id}
                color="error"
                variant="filled"
                label={`${term?.shortName || term?.name} rejected`}
                isTag
              />
            ))}
        </>
      }
      avatar={<IconLibrary iconName="SealCheck" size={24} />}
      actions={[
        {
          display: 'View consent history',
          callback: () =>
            openViewConsentsHistoryDialog({
              member,
              terms,
            }),
        },
      ]}
      fullWidth
      defaultClosed={determineDefaultClosed()}
      onToggle={onToggle}
      cardActionRightComponent={<LanguageSelector useToggleSwitch />}
      cardActions={[
        {
          text: 'Get link',
          variant: 'text',
          onClick: () => linkTextConsentsDialog({ userId: userId }),
        },
        {
          text: 'Send consents',
          variant: 'contained',
          onClick: () => openTextConsentsDialog({ userId: userId }),
        },
      ]}
    >
      <ConsentsList terms={terms} />
    </CollapsibleCard>
  );
};

export default ConsentInformationCard;
